import React, { useState, useEffect } from "react";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Typography,
    Box,
    Chip,
    FormControl,
    Select,
    MenuItem,
    TextField,
    CircularProgress,
    InputLabel
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import AlertIcon from "@mui/icons-material/WarningAmber";
import InfoIcon from "@mui/icons-material/InfoOutlined";
import Button from "../../../../shared/OAWorkflow/components/atoms/Button";
import { createTheme, ThemeProvider, useTheme } from "@mui/material/styles";
import { getEligibleUsers } from "../../../../shared/OAWorkflow/utils/taskTransitions";
import { axios_tenant } from "../../../../../axios";
import "../OAWorkflow.css";

// Helper to color-code status chips
const getStatusColor = (status) => {
    switch (status) {
        case "To Do": return { bg: "#F3F4F6", text: "#374151", border: "#E5E7EB" };
        case "In Progress": return { bg: "#EFF6FF", text: "#1D4ED8", border: "#BFDBFE" };
        case "Assign QA": return { bg: "#FEF3C7", text: "#D97706", border: "#FDE68A" };
        case "QC Pending": return { bg: "#ECFDF5", text: "#047857", border: "#A7F3D0" };
        case "Under Review": return { bg: "#F5F3FF", text: "#6D28D9", border: "#DDD6FE" };
        case "Completed": return { bg: "#F0FDF4", text: "#15803D", border: "#BBF7D0" };
        default: return { bg: "#F3F4F6", text: "#374151", border: "#E5E7EB" };
    }
};

const UpdateAssignmentModal = ({ open, onClose, task, users, onUpdateSuccess }) => {
    const [step, setStep] = useState("edit"); // "edit", "confirm"
    const [processorId, setProcessorId] = useState("");
    const [qaId, setQaId] = useState("");
    const [comments, setComments] = useState("");
    const [loading, setLoading] = useState(false);
    const [errorMsg, setErrorMsg] = useState("");

    // Invalid Assignee Dialog state
    const [invalidAssigneeOpen, setInvalidAssigneeOpen] = useState(false);

    // Reset fields on open
    useEffect(() => {
        if (open && task) {
            setStep("edit");
            const wasSkipped = !task.processorId && task.taskStatus !== 'To Do' && task.processorName === 'Processing Skipped';
            setProcessorId(wasSkipped ? "PROCESSING_SKIPPED" : (task.processorId || ""));
            setQaId(task.qaId || "");
            setComments("");
            setErrorMsg("");
        }
    }, [open, task]);

    const outerTheme = useTheme();
    const localTheme = React.useMemo(() => createTheme(outerTheme, {
        typography: {
            fontFamily: 'Lato, sans-serif',
        },
    }), [outerTheme]);

    if (!task) return null;

    const currentStatus = task.taskStatus;
    const isCompleted = currentStatus === "Completed";

    // Reassignment eligibility and lock checks
    const isProcessorLocked = ['QC Pending', 'Under Review', 'Completed'].includes(currentStatus);
    const isQaLocked = ['In Progress', 'Completed'].includes(currentStatus);

    // Filter eligible users using the shared transition utility
    const processors = getEligibleUsers(users, "Processor");
    const qas = getEligibleUsers(users, "QA");

    // Dynamic warning details
    const showProcessorWarning = currentStatus === "In Progress" && processorId !== "" && parseInt(processorId) !== task.processorId;
    const showQaWarning = currentStatus === "Under Review" && qaId !== "" && parseInt(qaId) !== task.qaId;

    // Check if anything has actually changed
    const getNormalizedProcessorId = (id) => id === "PROCESSING_SKIPPED" ? "" : id;
    const hasChanges = (getNormalizedProcessorId(processorId) !== (task.processorId || "")) || (qaId !== (task.qaId || ""));

    // Lookup user names
    const getProcessorName = (id, fallbackName) => {
        if (id === "SKIP_PROCESSING" || id === "PROCESSING_SKIPPED") return "Processing Skipped";
        if (!id && fallbackName === "Processing Skipped") return "Processing Skipped";
        const found = users.find(u => u.id === parseInt(id));
        return found ? found.displayName : "Unassigned";
    };

    const getQaName = (id) => {
        const found = users.find(u => u.id === parseInt(id));
        return found ? found.displayName : "Unassigned";
    };

    // Handle user selection changes with Active/Inactive verification
    const handleProcessorChange = (e) => {
        const val = e.target.value;
        if (val === "SKIP_PROCESSING" || val === "") {
            setProcessorId(val);
            return;
        }
        const selectedUser = users.find(u => u.id === parseInt(val));
        if (selectedUser && selectedUser.oaStatus !== "active") {
            setInvalidAssigneeOpen(true);
            return;
        }
        setProcessorId(val);
    };

    const handleQaChange = (e) => {
        const val = e.target.value;
        if (val === "") {
            setQaId(val);
            return;
        }
        const selectedUser = users.find(u => u.id === parseInt(val));
        if (selectedUser && selectedUser.oaStatus !== "active") {
            setInvalidAssigneeOpen(true);
            return;
        }
        setQaId(val);
    };

    const handlePreview = () => {
        if (!hasChanges) {
            setErrorMsg("No assignment changes were selected.");
            return;
        }
        setErrorMsg("");
        setStep("confirm");
    };

    const handleApply = async () => {
        setLoading(true);
        setErrorMsg("");
        try {
            await axios_tenant.patch(`/collaboration/workflow/tasks/${task.taskId}/assign`, {
                processorId: processorId === "SKIP_PROCESSING" ? "SKIP_PROCESSING" : ((processorId && processorId !== "PROCESSING_SKIPPED") ? parseInt(processorId) : null),
                qaId: qaId ? parseInt(qaId) : null,
                comments: comments.trim() || undefined
            }, { silent: true });
            setLoading(false);
            if (onUpdateSuccess) onUpdateSuccess();
            onClose();
        } catch (err) {
            setLoading(false);
            console.error("Assignment update failed:", err);
            setErrorMsg(err.response?.data?.message || "Failed to update task assignment.");
        }
    };

    // 1. Render Blocked Completed Screen
    if (isCompleted) {
        return (
            <ThemeProvider theme={localTheme}>
                <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: "8px", p: 1 } }}>
                    <DialogTitle sx={{ fontWeight: 600, color: "#0F172A", fontSize: "18px", pb: 1 }}>
                        Reassignment unavailable
                    </DialogTitle>
                    <DialogContent>
                        <Typography sx={{ color: "#475569", fontSize: "14px", lineHeight: "1.5" }}>
                            Completed tasks cannot be reassigned. Assignment controls remain disabled for Admin, Processor, and QA users once processing is complete.
                        </Typography>
                    </DialogContent>
                    <DialogActions sx={{ px: 3, pb: 2, justifyContent: "flex-end" }}>
                        <Button variant="secondary" onClick={onClose} height="36px">
                            Close
                        </Button>
                    </DialogActions>
                </Dialog>
            </ThemeProvider>
        );
    }

    const currentStatusColors = getStatusColor(currentStatus);

    return (
        <ThemeProvider theme={localTheme}>
            <Dialog open={open} onClose={loading ? undefined : onClose} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: "8px", p: 1 } }}>
                {step === "edit" ? (
                    <>
                        <DialogTitle sx={{ fontWeight: 600, color: "#0F172A", fontSize: "18px", pb: 1 }}>
                            Update Assignment
                        </DialogTitle>

                        <DialogContent sx={{ mt: 0.5 }}>
                            {/* Warnings / Preview */}
                            {(showProcessorWarning || showQaWarning) && (
                                <Box sx={{
                                    display: "flex",
                                    gap: 1.5,
                                    alignItems: "flex-start",
                                    bgcolor: "#FEF3C7",
                                    border: "1px solid #F59E0B",
                                    borderRadius: "6px",
                                    p: 1.5,
                                    mb: 2.5
                                }}>
                                    <AlertIcon sx={{ color: "#D97706", fontSize: 20, mt: 0.2 }} />
                                    <Box>
                                        <Typography sx={{ color: "#B45309", fontSize: "13px", fontWeight: 600 }}>
                                            Status Impact Preview:
                                        </Typography>
                                        {showProcessorWarning && (
                                            <Typography sx={{ color: "#B45309", fontSize: "13px", mt: 0.5 }}>
                                                Processor reassignment will move this In Progress task to To Do.
                                            </Typography>
                                        )}
                                        {showQaWarning && (
                                            <Typography sx={{ color: "#B45309", fontSize: "13px", mt: 0.5 }}>
                                                QA reassignment will move this Under Review task to QC Pending.
                                            </Typography>
                                        )}
                                    </Box>
                                </Box>
                            )}

                            {/* Task Info Metadata Card */}
                            <Box sx={{
                                border: "1px solid #E2E8F0",
                                borderRadius: "6px",
                                bgcolor: "#f3f8fc",
                                p: 2,
                                mb: 3,
                                display: "flex",
                                flexDirection: "column",
                                gap: 1.5
                            }}>
                                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                                    <Typography sx={{ fontSize: "12px", fontWeight: 600, color: "#475569" }}>
                                        Application/Patent No.
                                    </Typography>
                                    <Typography sx={{ fontSize: "13px", fontWeight: 600, color: "#0F172A" }}>
                                        {task.applicationNo || "—"}
                                    </Typography>
                                </Box>
                                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                                    <Typography sx={{ fontSize: "12px", fontWeight: 600, color: "#475569" }}>
                                        Current Status
                                    </Typography>
                                    <Chip
                                        label={currentStatus}
                                        size="small"
                                        sx={{
                                            bgcolor: currentStatusColors.bg,
                                            color: currentStatusColors.text,
                                            border: `1px solid ${currentStatusColors.border}`,
                                            fontWeight: 600,
                                            height: 22,
                                            fontSize: "11px"
                                        }}
                                    />
                                </Box>
                                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                                    <Typography sx={{ fontSize: "12px", fontWeight: 600, color: "#475569" }}>
                                        Current Owner
                                    </Typography>
                                    <Typography sx={{ fontSize: "13px", fontWeight: 500, color: "#0F172A" }}>
                                        {task.processorName || "Unassigned"} / {task.qaName || "Unassigned"}
                                    </Typography>
                                </Box>
                            </Box>

                            {/* Form Input fields */}
                            <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
                                <FormControl fullWidth size="small">
                                    <InputLabel id="proc-select-label">Processor</InputLabel>
                                    <Select
                                        labelId="proc-select-label"
                                        label="Processor"
                                        value={processorId}
                                        onChange={handleProcessorChange}
                                        disabled={isProcessorLocked}
                                    >

                                        {((processorId === "PROCESSING_SKIPPED") || (!task.processorId && task.taskStatus !== "To Do" && task.processorName === "Processing Skipped")) && (
                                            <MenuItem value="PROCESSING_SKIPPED" disabled sx={{ fontStyle: 'italic', color: '#6366F1' }}>
                                                Processing Skipped
                                            </MenuItem>
                                        )}
                                        {currentStatus === "To Do" && (
                                            <MenuItem value="SKIP_PROCESSING">
                                                <Typography sx={{ color: "#6366F1", fontStyle: "italic", fontWeight: 500 }}>
                                                    Skip Processing
                                                </Typography>
                                            </MenuItem>
                                        )}
                                        {processors.map((user) => {
                                            const isActive = user.oaStatus === "active";
                                            return (
                                                <MenuItem key={user.id} value={user.id}>
                                                    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", width: "100%" }}>
                                                        <Typography sx={{ fontSize: "14px" }}>{user.displayName}</Typography>
                                                        <Chip
                                                            label={isActive ? "Active" : "Inactive"}
                                                            size="small"
                                                            color={isActive ? "success" : "error"}
                                                            variant="outlined"
                                                            sx={{ height: 20, fontSize: "10px", pointerEvents: "none" }}
                                                        />
                                                    </Box>
                                                </MenuItem>
                                            );
                                        })}
                                    </Select>
                                </FormControl>

                                <FormControl fullWidth size="small">
                                    <InputLabel id="qa-select-label">QA</InputLabel>
                                    <Select
                                        labelId="qa-select-label"
                                        label="QA"
                                        value={qaId}
                                        onChange={handleQaChange}
                                        disabled={isQaLocked}
                                    >

                                        {qas.map((user) => {
                                            const isActive = user.oaStatus === "active";
                                            return (
                                                <MenuItem key={user.id} value={user.id}>
                                                    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", width: "100%" }}>
                                                        <Typography sx={{ fontSize: "14px" }}>{user.displayName}</Typography>
                                                        <Chip
                                                            label={isActive ? "Active" : "Inactive"}
                                                            size="small"
                                                            color={isActive ? "success" : "error"}
                                                            variant="outlined"
                                                            sx={{ height: 20, fontSize: "10px", pointerEvents: "none" }}
                                                        />
                                                    </Box>
                                                </MenuItem>
                                            );
                                        })}
                                    </Select>
                                </FormControl>

                                <TextField
                                    label="Reassignment Comments / Notes"
                                    placeholder="Specify the reason for reassignment..."
                                    multiline
                                    rows={3}
                                    fullWidth
                                    value={comments}
                                    onChange={(e) => setComments(e.target.value)}
                                />
                            </Box>

                            {errorMsg && (
                                <Typography sx={{ color: "#EF4444", fontSize: "13px", mt: 2, fontWeight: 500 }}>
                                    {errorMsg}
                                </Typography>
                            )}
                        </DialogContent>

                        <DialogActions sx={{ borderTop: "1px solid #E2E8F0", px: 3, py: 2, mt: 1 }}>
                            <Button variant="secondary" onClick={onClose} height="36px">
                                Cancel
                            </Button>
                            <Button variant="primary" onClick={handlePreview} disabled={!hasChanges} height="36px">
                                Preview Update
                            </Button>
                        </DialogActions>
                    </>
                ) : (
                    <>
                        <DialogTitle sx={{ fontWeight: 600, color: "#0F172A", fontSize: "18px", pb: 1 }}>
                            Confirm assignment update
                        </DialogTitle>

                        <DialogContent sx={{ mt: 1 }}>
                            <Box sx={{ border: "1px solid #E2E8F0", borderRadius: "6px", p: 2, bgcolor: "#f3f8fc", mb: 3 }}>
                                <Typography sx={{ fontWeight: 600, fontSize: "13px", color: "#475569", mb: 1.5 }}>
                                    Proposed Changes:
                                </Typography>

                                {processorId !== (task.processorId || "") && (
                                    <Box sx={{ mb: 1, display: "flex", gap: 1 }}>
                                        <Typography sx={{ fontSize: "13px", fontWeight: 600, color: "#1E293B", minWidth: 100 }}>
                                            Processor:
                                        </Typography>
                                        <Typography sx={{ fontSize: "13px", color: "#64748B" }}>
                                            {getProcessorName(task.processorId, task.processorName)} → {getProcessorName(processorId)}
                                        </Typography>
                                    </Box>
                                )}

                                {qaId !== (task.qaId || "") && (
                                    <Box sx={{ mb: 1, display: "flex", gap: 1 }}>
                                        <Typography sx={{ fontSize: "13px", fontWeight: 600, color: "#1E293B", minWidth: 100 }}>
                                            QA:
                                        </Typography>
                                        <Typography sx={{ fontSize: "13px", color: "#64748B" }}>
                                            {getQaName(task.qaId)} → {getQaName(qaId)}
                                        </Typography>
                                    </Box>
                                )}

                                {(showProcessorWarning || showQaWarning) && (
                                    <Box sx={{ mb: 1, display: "flex", gap: 1 }}>
                                        <Typography sx={{ fontSize: "13px", fontWeight: 600, color: "#1E293B", minWidth: 100 }}>
                                            Status:
                                        </Typography>
                                        <Typography sx={{ fontSize: "13px", color: "#D97706", fontWeight: 600 }}>
                                            {currentStatus} → {showProcessorWarning ? "To Do" : "QC Pending"}
                                        </Typography>
                                    </Box>
                                )}

                                <Box sx={{ display: "flex", gap: 1, mt: 1.5, borderTop: "1px solid #E2E8F0", pt: 1.5 }}>
                                    <Typography sx={{ fontSize: "13px", fontWeight: 600, color: "#1E293B", minWidth: 100 }}>
                                        Comments:
                                    </Typography>
                                    <Typography sx={{ fontSize: "13px", color: "#334155", fontStyle: comments ? "normal" : "italic" }}>
                                        {comments.trim() || "No comments provided."}
                                    </Typography>
                                </Box>
                            </Box>

                            <Box sx={{ display: "flex", gap: 1.5, alignItems: "flex-start", p: 1.5, border: "1px solid #E2E8F0", borderRadius: "6px", bgcolor: "#F0F9FF" }}>
                                <InfoIcon sx={{ color: "#0284C7", fontSize: 20, mt: 0.2 }} />
                                <Typography sx={{ color: "#0369A1", fontSize: "13px", lineHeight: "1.5" }}>
                                    Audit Warning: previous assignee, new assignee, updated by, updated on, and comments will be tracked in the task audit history.
                                </Typography>
                            </Box>

                            {errorMsg && (
                                <Typography sx={{ color: "#EF4444", fontSize: "13px", mt: 2, fontWeight: 500 }}>
                                    {errorMsg}
                                </Typography>
                            )}
                        </DialogContent>

                        <DialogActions sx={{ borderTop: "1px solid #E2E8F0", px: 3, py: 2, mt: 1 }}>
                            <Button variant="secondary" onClick={() => setStep("edit")} disabled={loading} height="36px">
                                Cancel
                            </Button>
                            <Button variant="primary" onClick={handleApply} disabled={loading} height="36px">
                                {loading ? <CircularProgress size={20} color="inherit" /> : "Apply Update"}
                            </Button>
                        </DialogActions>
                    </>
                )}
            </Dialog>

            {/* Inactive User / Block Validation Dialog */}
            <Dialog
                open={invalidAssigneeOpen}
                onClose={() => setInvalidAssigneeOpen(false)}
                maxWidth="xs"
                fullWidth
                PaperProps={{ sx: { borderRadius: "8px", p: 1 } }}
            >
                <DialogTitle sx={{ fontWeight: 600, color: "#EF4444", fontSize: "16px", pb: 1 }}>
                    Unable to assign selected user
                </DialogTitle>
                <DialogContent>
                    <Typography sx={{ color: "#475569", fontSize: "13.5px", lineHeight: "1.5" }}>
                        Validation failed. Only active users from the same tenant/client context can be assigned; inactive or unauthorized users are blocked.
                    </Typography>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2, justifyContent: "flex-end" }}>
                    <Button variant="secondary" onClick={() => setInvalidAssigneeOpen(false)} height="32px">
                        Close
                    </Button>
                </DialogActions>
            </Dialog>
        </ThemeProvider>
    );
};

export default UpdateAssignmentModal;
